import { useMutation } from "@tanstack/react-query";
import { markFeedbackReviewed, type ApiError } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";

/**
 * "Mark reviewed" mutation for the `/feedback` surfaces (M7).
 *
 * Marks a note OR playlist reviewed via `POST /api/my-feedback/
 * review` (clips have no review tracking — the backend rejects a
 * clip id). On success it invalidates the `["my-feedback"]`
 * bundle so the Reviewed/New pills refresh.
 *
 * The backend re-validates visibility at mark-time, so a guessed
 * id 403s rather than recording a review — the client doesn't
 * need to pre-check.
 */
export function useFeedbackReview() {
  const mutation = useMutation({
    mutationFn: (body: { note_id?: number; playlist_id?: number }) =>
      markFeedbackReviewed(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-feedback"] });
    },
  });
  return {
    markReviewed: (body: { note_id?: number; playlist_id?: number }) =>
      mutation.mutateAsync(body),
    isPending: mutation.isPending,
    error: (mutation.error as ApiError | null) ?? null,
  };
}
